<template>
  <div class="sensors">
    <Loading v-if="loading || !chartLoaded" />
    <Title :title="$t('sensors')" />
    <div class="sensors__header">
      <div class="container">
        <Dropdown
          class="sensors__header-dropdown"
          :selected-option="selectedSensors.selectedOption"
          :options="selectedSensors.options"
          data-testid="selectSensors"
          :multiple="true"
          @select-option="selectSensors"
        />
        <Button
          class="checkAllTemperatures-button"
          variant="filled"
          :title="$t('checkAllTemperatures')"
          @click="initCheckAllTemperatures"
        />
        <!-- <Dropdown
          v-if="sensorsOptions[0]?.value === 'all'"
          class="sensors__header-dropdown"
          :selected-option="selectedSortAllSensors.selectedOption"
          :options="selectedSortAllSensors.options"
          data-testid="selectSortAllSensors"
          @select-option="selectSortAllSensors"
        /> -->
      </div>
      <div class="chooseDate">
        <div class="date-block">
          <div class="date-inputs">
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.startTime"
                name="startTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.endTime"
                name="endTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
          </div>
          <p v-if="!isDateCorrect" class="date-error">
            {{ $t('errorDate') }}
          </p>
        </div>
      </div>
    </div>
    <SensorsMain
      :pharmacy="pharmacy"
      :line-chart-key="lineChartKey"
      :data-line-chart="dataLineChart"
      :alarms-sensor="alarmsSensor"
      :is-date-correct="isDateCorrect"
      :selected-sensors-data="selectedSensorsData"
      :selected-option="selectedSensors.selectedOption"
      :temperature-sensor-stamp="temperatureSensorStamp"
    />
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import SensorsMain from './SensorsMain/SensorsMain.vue';

import { format } from 'date-fns';

import {
  AlarmSensor,
  DataCreateNewSensor,
  DataSensor,
  ResponsesSensor,
  Sensor
} from '@/store/modules/pharmacy/types';
import { InputFileEvent, Option } from '@/types';

const transformationDate = new Date().setDate(new Date().getDate() - 3);

@Options({
  name: 'Sensors',
  components: {
    Loading,
    SensorsMain
  },
  data() {
    return {
      selectedSensors: {
        selectedOption: [],
        options: {}
      },
      allSensors: {
        value: 'all',
        label: this.$t('all')
      },
      dateRange: {
        startTime: format(new Date(transformationDate), 'dd/MM/yyyy'),
        endTime: format(new Date(), 'dd/MM/yyyy')
      },
      sensorId: '',
      alarmsSensor: null,
      lineChartKey: 0,
      activeSensor: null,
      dataLineChart: null,
      selectedOption: null,
      sensorsOptions: null,
      selectedSensorsData: null,
      loading: false,
      chartLoaded: false,
      checkedSensor: false,
      showTimePanel: false
    };
  },
  created() {
    if (this.user) {
      this.show();
    }
  },
  updated() {
    if (this.sensors) {
      this.selectedSensors.selectedOption = this.sensorsOptions;
    }
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy', 'sensor', 'temperatureSensorStamp']),
    isDateCorrect() {
      if (this.isDatePresent) {
        const startTime = this.dateRange.startTime.split('/');
        const endTime = this.dateRange.endTime.split('/');

        const defaultParams = {
          ...this.dateRange,
          startTime: new Date(
            +startTime[2],
            startTime[1] - 1,
            +startTime[0]
          ).valueOf(),
          endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
        };

        return defaultParams.endTime >= defaultParams.startTime;
      }

      return true;
    },
    isDatePresent() {
      const startTime = this.dateRange.startTime.split('/');
      const endTime = this.dateRange.endTime.split('/');

      const defaultParams = {
        ...this.dateRange,
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
      };

      return defaultParams && defaultParams.startTime && defaultParams.endTime;
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPharmacy',
      'getSensor',
      'getTemperatureSensorStamp',
      'sendTemperatureSensorsStamp',
      'getAlarmsSensor'
    ]),
    async show() {
      await this.loadSensors();
      this.initSensorsOptions();
    },
    async loadSensors() {
      this.loading = true;

      await this.getPharmacy(this.user.pharmacyId);

      this.loading = false;
    },
    initSensorsOptions() {
      if (this.pharmacy) {
        const sensorsOptionsData = this.pharmacy?.sensors.map(
          (i: DataCreateNewSensor) => ({
            value: i.id,
            label: i.name
          })
        );

        this.sensorsOptions = [...sensorsOptionsData, this.allSensors];
        this.selectedSensors.options = this.sensorsOptions;
        this.selectedSensors.selectedOption = [this.sensorsOptions[0]];

        this.selectedSensor = this.sensorsOptions[0];
        this.activeSensor = this.selectedSensors.selectedOption;
      }
    },
    async initAlarmsSensor() {
      this.loading = true;
      this.buttonLoaded = false;

      let selectedIds = this.selectedSensors.selectedOption?.map(
        (i: Option) => i.value
      );

      const isAll = selectedIds.includes('all');

      if (selectedIds[0] === 'all') {
        selectedIds = this.selectedSensors.options
          .filter((opt: Option) => opt.value !== 'all')
          .map((opt: Option) => opt.value);
      }

      const matchedSensors = this.pharmacy?.sensors.filter((sensor: Sensor) =>
        selectedIds.includes(sensor.id)
      );

      let sensorsSn = matchedSensors.map((i: Sensor) => i.sn);

      if (isAll) {
        selectedIds = this.pharmacy.sensors.map((s: Sensor) => s.id);
        sensorsSn = this.pharmacy.sensors.map((s: Sensor) => s.sn);
      }

      const startTime = this.dateRange.startTime.split('/');

      this.getTotalEndTime();

      const defaultParams = {
        ...this.dateRange,
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(
          +this.totalEndTime[2],
          +this.totalEndTime[1] - 1,
          +this.totalEndTime[0],
          +this.totalEndTime[3],
          +this.totalEndTime[4],
          +this.totalEndTime[5]
        ).valueOf()
      };

      if (
        sensorsSn &&
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        const snToIdMap = Object.fromEntries(
          matchedSensors.map((sensor: Sensor) => [sensor.sn, sensor.id])
        );

        const responses = await Promise.all(
          sensorsSn.map((sensorSn: string) => {
            const id = snToIdMap[sensorSn];
            return this.getAlarmsSensor({ ...defaultParams, sensorSn, id });
          })
        );

        if (isAll) {
          const merged = responses.flatMap((sensor: ResponsesSensor) => {
            const sensorId = sensor.id;

            if (!Array.isArray(sensor.alarmsSensor)) return [];

            return sensor.alarmsSensor.map((alarm: AlarmSensor) => ({
              ...alarm,
              id: sensorId
            }));
          });

          this.alarmsSensor = merged;
        } else {
          this.alarmsSensor = responses.map((sensor: DataSensor) => {
            const cloned = JSON.parse(JSON.stringify(sensor));
            cloned.id = sensor.id;

            return cloned;
          });
        }
      }

      this.loading = false;
    },
    getRandomColorFromScale() {
      const colorScale = [
        '#E2523F',
        '#E8633A',
        '#ED7436',
        '#F18537',
        '#F39737',
        '#F19E37',
        '#E29B49',
        '#C48F5C',
        '#A3836A',
        '#738C7B',
        '#47987F'
      ];

      const index = Math.floor(Math.random() * colorScale.length);
      return colorScale[index];
    },
    async initTemperatureSensorStamp(sensorId: string) {
      this.loading = true;

      await this.getTemperatureSensorStamp({ boxId: sensorId, last: true });

      if (this.temperatureSensorStamp) {
        this.checkedSensor = true;
      }

      this.loading = false;
    },
    async loadSensor() {
      this.chartLoaded = false;
      this.loading = true;

      let sensorIds = this.selectedSensors.selectedOption?.map(
        (i: Option) => i.value
      );

      const isAll = sensorIds.includes('all');

      if (isAll) {
        sensorIds = this.pharmacy.sensors.map((s: Sensor) => s.id);
      }

      const startTime = this.dateRange.startTime.split('/');
      this.getTotalEndTime();

      const defaultParams = {
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(
          +this.totalEndTime[2],
          +this.totalEndTime[1] - 1,
          +this.totalEndTime[0],
          +this.totalEndTime[3],
          +this.totalEndTime[4],
          +this.totalEndTime[5]
        ).valueOf(),
        pharmacyId: this.user.pharmacyId
      };

      if (this.isDateCorrect) {
        const responses = await Promise.all(
          sensorIds.map((sensorId: string) =>
            this.getSensor({ ...defaultParams, sensorId })
          )
        );

        if (isAll) {
          const base = JSON.parse(JSON.stringify(responses[0]));
          base.id = 'all';

          base.datasets = responses.map((sensor: DataSensor) => {
            const cloned = JSON.parse(JSON.stringify(sensor.datasets[0]));
            const randomColor = this.getRandomColorFromScale();

            cloned.label = sensor.datasets[0].label;
            cloned.borderColor = randomColor;
            cloned.backgroundColor = randomColor;

            return cloned;
          });

          this.dataLineChart = base;
        } else {
          this.dataLineChart = responses.map((sensor: DataSensor) => {
            const cloned = JSON.parse(JSON.stringify(sensor));
            const randomColor = this.getRandomColorFromScale();

            this.initTemperatureSensorStamp(cloned.datasets[0].value);

            cloned.id = cloned.datasets[0].value;
            cloned.datasets[0].backgroundColor = randomColor;
            cloned.datasets[0].borderColor = randomColor;

            return cloned;
          });
        }

        this.lineChartKey++;
      }

      this.chartLoaded = true;
      this.loading = false;
    },
    getTotalEndTime() {
      const arrayEndTime = this.dateRange.endTime.split('/');
      const arrayToday = format(new Date(), 'dd/MM/yyyy/HH/mm/ss').split('/');
      const arrayHour = arrayToday.slice(-3);

      this.totalEndTime = arrayEndTime.concat(arrayHour);
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;

      this.dateRange = {
        ...this.dateRange,
        [name]: value
      };
    },
    selectSensors(options: Option[]) {
      const wasAll = options[0].value === 'all';

      if (options[0].value !== 'all') {
        const hasAll = options.some((opt) => opt.value === 'all');

        if (hasAll) {
          this.selectedSensors.selectedOption = [
            this.sensorsOptions.find((o: Option) => o.value === 'all')
          ];

          this.activeSensor = null;
          return;
        }
      }

      if (wasAll) {
        this.selectedSensors.selectedOption = options.filter(
          (opt) => opt.value !== 'all'
        );
      } else {
        this.selectedSensors.selectedOption = options;
      }

      if (this.selectedSensors.selectedOption.length === 1) {
        const id = this.selectedSensors.selectedOption[0].value;

        this.activeSensor =
          this.pharmacy?.sensors?.find(
            (i: DataCreateNewSensor) => i.id === id
          ) || null;
      } else {
        this.activeSensor = null;
      }
    },
    closeTimePanel() {
      this.showTimePanel = false;

      this.initSensorsOptions();
    },
    async initCheckAllTemperatures() {
      const sensorIds = this.pharmacy.sensors.map((s: Sensor) => s.sn);

      for (const id of sensorIds) {
        this.loading = true;

        await this.sendTemperatureSensorsStamp({ boxId: id });

        this.loading = false;
      }
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.show();
      }
    },
    'selectedSensors.selectedOption': {
      immediate: true,
      handler(options: Option[]) {
        if (!Array.isArray(options)) return;

        const selectedIds = options.map((el: Option) => el.value);

        if (this.pharmacy) {
          if (selectedIds.includes('all')) {
            this.selectedSensorsData = this.pharmacy.sensors;
          } else {
            this.selectedSensorsData = this.pharmacy.sensors.filter(
              (sensor: Sensor) => selectedIds.includes(sensor.id)
            );
          }

          this.loadSensor();
          this.initAlarmsSensor();
        }
      }
    }
  }
})
export default class Sensors extends Vue {}
</script>

<style lang="scss" scoped>
.sensors {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 56px);
  padding: 24px;
  .sensors__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 15px;
    padding: 24px 0;
    .container {
      display: flex;
      align-items: center;
      .sensors__header-dropdown {
        height: 40px;
        width: 250px !important;
      }
      .checkAllTemperatures-button {
        margin: 0 12px;
      }
    }
    .chooseDate {
      display: flex;
      height: 42px;
      .date-block {
        width: 100%;
        height: 100%;
        margin-left: 10px;
        .date-inputs {
          display: flex;
          align-items: center;
          .date-inputs-button {
            width: 60px;
            margin-right: 10px;
            z-index: 101;
            cursor: pointer;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            margin-bottom: 0 !important;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
        .date-error {
          color: $red;
          font-size: $fourteenSize;
          margin-top: 10px;
        }
      }
    }
  }
}
</style>
